import React from 'react';
import type { HeadlineFont } from '../../../services/storiesAdminService';

interface SlidePreviewProps {
  backgroundGradient: string;
  textColor: string;
  eyebrow?: string | null;
  headline: string;
  body?: string | null;
  imageUrl?: string | null;
  headlineFont: HeadlineFont;
  ctaLabel?: string | null;
  ctaUrl?: string | null;
  /** Прогресс-бар сверху (имитация stories progress). По умолчанию 1 слайд. */
  totalSlides?: number;
  activeSlideIndex?: number;
}

/**
 * Phone-shaped preview pane для одного слайда. Тот же визуальный язык,
 * что у пользовательского viewer (gradient + emoji/image + text + cta).
 * Размер: 280×560 (масштаб от 360×720) — помещается в правую колонку
 * редактора без скролла.
 */
const SlidePreview: React.FC<SlidePreviewProps> = ({
  backgroundGradient,
  textColor,
  eyebrow,
  headline,
  body,
  imageUrl,
  headlineFont,
  ctaLabel,
  ctaUrl,
  totalSlides = 1,
  activeSlideIndex = 0,
}) => {
  const fontFamily =
    headlineFont === 'serif'
      ? `'Georgia', 'Times New Roman', serif`
      : `'Inter', system-ui, -apple-system, sans-serif`;

  return (
    <div
      className="relative rounded-[36px] overflow-hidden shadow-2xl border-[6px] border-gray-900 mx-auto"
      style={{
        width: 280,
        height: 560,
        background: backgroundGradient || 'linear-gradient(135deg, #444, #222)',
        color: textColor || '#FFFFFF',
      }}
    >
      {/* progress bars */}
      <div className="absolute top-3 left-3 right-3 flex gap-1 z-10">
        {Array.from({ length: Math.max(1, totalSlides) }).map((_, i) => (
          <div
            key={i}
            className="flex-1 h-0.5 rounded-full bg-white/30 overflow-hidden"
          >
            <div
              className="h-full bg-white"
              style={{
                width: i < activeSlideIndex ? '100%' : i === activeSlideIndex ? '40%' : '0%',
              }}
            />
          </div>
        ))}
      </div>

      {/* image (if set) — рендерится поверх gradient */}
      {imageUrl && (
        <img
          src={imageUrl}
          alt=""
          className="absolute inset-0 w-full h-full object-cover opacity-90"
          style={{ pointerEvents: 'none' }}
        />
      )}

      {/* content — bottom-aligned, padded */}
      <div className="absolute inset-0 flex flex-col justify-end p-5 z-[2]">
        {eyebrow && (
          <div
            className="text-[11px] uppercase tracking-wider mb-2 opacity-80"
            style={{ color: textColor }}
          >
            {eyebrow}
          </div>
        )}
        <div
          className="text-[22px] font-bold leading-tight mb-2"
          style={{ color: textColor, fontFamily }}
        >
          {headline || 'Заголовок слайда'}
        </div>
        {body && (
          <div
            className="text-[13px] leading-snug opacity-90 mb-3"
            style={{ color: textColor }}
          >
            {body}
          </div>
        )}
        {ctaLabel && (
          <button
            type="button"
            className="self-start px-4 py-2 rounded-full text-[13px] font-semibold backdrop-blur-sm"
            style={{
              background: 'rgba(255,255,255,0.18)',
              color: textColor,
              border: `1px solid ${textColor}33`,
            }}
            title={ctaUrl || undefined}
          >
            {ctaLabel}
          </button>
        )}
      </div>
    </div>
  );
};

export default SlidePreview;
